/**
 * ProductRecommendationsServer — server-rendered variant of the recs carousel.
 *
 * Same look-and-feel as the client-side `ProductRecommendations` at
 * `~/hec/components/recs/product-recommendations.tsx`, but renders from
 * a pre-fetched products array (no CIO client SDK call, no SCAPI fetcher).
 *
 * Use this when the loader can fetch the recs server-side (via
 * `cioRecommendations` + `getProductRecommendations`) and pass the
 * resolved data straight in. Renders on first paint, no hydration flicker.
 *
 * Architecture: matches CIO Path A spike §6 row 10 + §8 footnote — PDP
 * CYP / Related Products may stay server-only for June launch.
 */
import { H2 } from "~/components/ui/text";
import SvgArrow from "~/hec/assets/icons/arrow";
import { RecsCarouselTile } from "~/hec/components/recs/recs-carousel-tile";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "~/hec/components/ui/carousel";
import { useTrackRecsClick } from "~/hec/hooks/use-track-recs-click";
import { useTrackRecsView } from "~/hec/hooks/use-track-recs-view";
import type { HecProduct } from "~/hec/types/product";
import { cn } from "~/lib/utils";

interface ProductRecommendationsServerProps {
  products: HecProduct[];
  heading?: string;
  className?: string;
  /** Pod ID — required for `recommendation_click` beacon attribution. */
  podId?: string;
  /** `result_id` from the CIO response that produced `products`. */
  resultId?: string;
  /** Per-product strategy IDs (parallel to `products`). */
  strategyIds?: string[];
}

export function ProductRecommendationsServer({
  products,
  heading,
  className,
  podId,
  resultId,
  strategyIds,
}: ProductRecommendationsServerProps) {
  const trackClick = useTrackRecsClick();
  const viewRef = useTrackRecsView({
    podId,
    resultId,
    itemIds: products.map(product => product.id),
  });
  if (products.length === 0) return null;

  return (
    <div ref={viewRef} className={cn("products-recommendations", className)}>
      {heading && (
        <H2 className="text-brand-primary-black leading-lg mb-[24px] border-b-0 pb-0 font-serif text-[1.375rem] font-semibold">
          {heading}
        </H2>
      )}
      <Carousel className="mt-4" opts={{ slidesToScroll: 1, align: "start" }}>
        <CarouselPrevious
          hideWhenDisabled
          overlapContent={true}
          buttonIcon={SvgArrow}
          iconAttributes={{ className: "h-5 w-[23px] -scale-x-100" }}
          className="left-0 h-[50px] w-[50px] border border-blue-04 bg-white text-blue-04 lg:-left-[25px]"
          data-testid="product-recommendations-server-nav"
        />
        <CarouselContent className="-ml-5">
          {products.map((product, i) => (
            <CarouselItem
              key={`${product.id}-${i}`}
              slidesVisible="4"
              slidePadding="0"
              slidesVisibleMobile="2"
              slidePaddingMobile="0"
              className="pl-5 lg:pl-5"
            >
              {/* onClickCapture intercepts clicks before they reach the tile's
                  internal <Link> so we fire `recommendation_click` to CIO
                  without blocking navigation. */}
              <div
                onClickCapture={() => {
                  if (!podId) return;
                  trackClick({
                    podId,
                    itemId: product.id,
                    itemName: product.name ?? product.id,
                    strategyId: strategyIds?.[i],
                    resultId,
                    resultPositionOnPage: i + 1,
                    numResultsPerPage: products.length,
                  });
                }}
              >
                <RecsCarouselTile
                  product={product}
                  gtmListId={podId}
                  gtmListName={heading}
                  gtmIndex={i + 1}
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselNext
          hideWhenDisabled
          overlapContent={true}
          buttonIcon={SvgArrow}
          iconAttributes={{ className: "h-5 w-[23px]" }}
          className="right-0 h-[50px] w-[50px] border border-blue-04 bg-white text-blue-04 lg:-right-[25px]"
          data-testid="product-recommendations-server-nav"
        />
      </Carousel>
    </div>
  );
}
